/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import * as React from "react"
import { useSelector, useDispatch } from "react-redux"
import { AnimatePresence, motion } from "framer-motion"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import CookieBanner from "@components/global/CookieBanner"
import PreQualifyBanner from "@components/global/PrequalifyBanner"
import Modal from "@components/global/Modal"
import CookiePopup from "@components/global/CookiePopup"
import ClientSideOnly from "@components/global/client-side-only"

/* -------------------------------------------------------------------------- */
/*                                    Utils                                   */
/* -------------------------------------------------------------------------- */
import {
  essentialConsentTypes,
  grantGaConsent,
  nonEssentialConsentTypes,
} from "@utils/gtm"

/* -------------------------------------------------------------------------- */
/*                                External Data                               */
/* -------------------------------------------------------------------------- */
import { storedFunnelName as purchasePrequalifyFunnelName } from "../../pages/purchase-prequalify"
import { storedFunnelName as refinancePrequalifyFunnelName } from "../../pages/refinance-prequalify"

/* -------------------------------------------------------------------------- */
/*                                    Redux                                   */
/* -------------------------------------------------------------------------- */
import { update } from "@state/showCookieSettingsSlice"

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function GlobalBanner(props) {
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const [storedData, setStoredData] = React.useState({
    consentTypes: null,
    prequalifyFunnelName: null,
  })
  const [showBanners, setShowBanners] = React.useState({
    cookieConsent: true,
    prequalifyFunnel: false,
  })

  const storeConsentTypes = consentTypes => {
    localStorage.setItem("gaConsentTypes", JSON.stringify(consentTypes))
    grantGaConsent({ consentTypes, window })
    setStoredData(prevStoredData => ({
      ...prevStoredData,
      consentTypes,
    }))
    if (showBanners.cookieConsent)
      setShowBanners(prevShowBanners => ({
        ...prevShowBanners,
        cookieConsent: false,
      }))
  }

  const initialValuesSet = React.useRef(false)

  React.useEffect(() => {
    if (!initialValuesSet.current) {
      let currentStoredData = structuredClone(storedData)
      let bannersToShow = structuredClone(showBanners)
      const storedConsentTypes = localStorage.getItem("gaConsentTypes")

      if (storedConsentTypes) {
        currentStoredData["consentTypes"] = JSON.parse(storedConsentTypes)
        bannersToShow["cookieConsent"] = false

        grantGaConsent({
          consentTypes: currentStoredData["consentTypes"],
          window,
        })
      }

      const storedPurchasePrequalifyFunnelData = localStorage.getItem(
        purchasePrequalifyFunnelName,
      )

      if (
        storedPurchasePrequalifyFunnelData ||
        localStorage.getItem(refinancePrequalifyFunnelName)
      ) {
        currentStoredData["prequalifyFunnelName"] =
          storedPurchasePrequalifyFunnelData
            ? purchasePrequalifyFunnelName
            : refinancePrequalifyFunnelName
        bannersToShow["prequalifyFunnel"] = true
      }
      initialValuesSet.current = true
      setStoredData(currentStoredData)
      setShowBanners(bannersToShow)
    }
  }, [storedData, showBanners])

  const dispatch = useDispatch()
  const showCookieSettings = useSelector(state => state?.showCookieSettings)
    ?.value

  React.useEffect(() => {
    if (showCookieSettings) {
      setIsModalOpen(true)
    }
  }, [showCookieSettings])

  React.useEffect(() => {
    if (!isModalOpen) {
      dispatch(update(false))
    }
  }, [isModalOpen])

  return (
    <ClientSideOnly>
      {showBanners.cookieConsent && (
        <CookieBanner
          storeConsentTypes={storeConsentTypes}
          storedConsentTypes={storedData.consentTypes}
          openCookiePopup={() => setIsModalOpen(true)}
        />
      )}
      <Modal closeModal={() => setIsModalOpen(false)} isModalOpen={isModalOpen}>
        <CookiePopup
          closeModal={() => {
            setIsModalOpen(false)
          }}
          storeConsentTypes={storeConsentTypes}
          storedConsentTypes={storedData.consentTypes}
          consentTypes={{
            essential: essentialConsentTypes,
            nonEssential: {
              marketing: nonEssentialConsentTypes.marketing,
            },
          }}
        />
      </Modal>
      <AnimatePresence>
        {showBanners.prequalifyFunnel &&
          !showBanners.cookieConsent &&
          !props.isFunnel && (
            <motion.div
              key="prequalify-banner"
              initial={{
                y: "100%",
              }}
              animate={{
                transition: {
                  delay: 1,
                  duration: 0.5,
                  ease: "linear",
                },
                y: 0,
              }}
              transition={{
                duration: 0.5,
                ease: "linear",
              }}
              exit={{
                y: "100%",
              }}
              className="fixed bottom-0 w-full z-[100] [&_>_*]:relative"
            >
              <PreQualifyBanner
                funnelPath={
                  storedData.prequalifyFunnelName ===
                  purchasePrequalifyFunnelName
                    ? `/purchase-prequalify`
                    : `/refinance-prequalify`
                }
                closeBanner={() =>
                  setShowBanners(prevShowBanners => ({
                    ...prevShowBanners,
                    prequalifyFunnel: false,
                  }))
                }
              />
            </motion.div>
          )}
      </AnimatePresence>
    </ClientSideOnly>
  )
}
